/**
 * Hook to get the driver labels for the assigned and unassigned drivers
 * - Eg labels:
 *  - Trip X - Driver Y:
 *  - Trip X - Driver Y dispatch:
 *  - Driver Y dispatch:
 *  - Driver Y unassigned:
 *  - Send to:
 * @param isSplitLoad - Whether the load is a split load
 * @param assignedDrivers - The assigned drivers
 * @param unassignedDrivers - The unassigned drivers
 * @returns The driver labels for the assigned and unassigned drivers
 */
import { useCallback } from 'react';
import { INotifyDriverWithFullSegment } from '../TwNotifyDriver';

interface UseDriverLabelsProps {
  isSplitLoad: boolean;
  assignedDrivers: INotifyDriverWithFullSegment[];
  unassignedDrivers: INotifyDriverWithFullSegment[];
}

const useDriverLabels = ({
  isSplitLoad,
  assignedDrivers,
  unassignedDrivers
}: UseDriverLabelsProps): {
  getAssignedDriverLabel: (driver: INotifyDriverWithFullSegment) => string;
  getUnassignedDriverLabel: (driver: INotifyDriverWithFullSegment) => string;
} => {
  const getAssignedDriverLabel = useCallback(
    (driver: INotifyDriverWithFullSegment): string => {
      if (isSplitLoad) {
        const filteredDrivers = assignedDrivers.filter((dr) => dr.driverId !== driver.driverId);
        const isTeamDriver = filteredDrivers.find(
          (dr) => dr.fullSegment.id === driver.fullSegment.id
        );

        if (isTeamDriver) {
          return `Trip ${driver.fullSegment.sort_order + 1} - Driver ${driver.driverIndex + 1}:`;
        } else {
          return `Trip ${driver.fullSegment.order_readable_id}-${
            driver.fullSegment.sort_order + 1
          } dispatch:`;
        }
      } else {
        if (assignedDrivers.length > 1) {
          return `Driver ${driver.driverIndex + 1} dispatch:`;
        } else {
          return 'Send to:';
        }
      }
    },
    [isSplitLoad, assignedDrivers]
  );

  const getUnassignedDriverLabel = useCallback(
    (driver: INotifyDriverWithFullSegment): string => {
      if (isSplitLoad) {
        return `Trip ${driver.fullSegment.sort_order + 1} unassigned driver:`;
      } else {
        if (unassignedDrivers.length > 1) {
          return `Driver ${driver.driverIndex + 1} unassigned:`;
        } else {
          return 'Unassigned driver:';
        }
      }
    },
    [isSplitLoad, unassignedDrivers]
  );

  return { getAssignedDriverLabel, getUnassignedDriverLabel };
};

export default useDriverLabels;
